import React, { useCallback, useMemo } from "react";
import { ArrowDownOutlined, ArrowUpOutlined } from "@ant-design/icons";
import { Card, Descriptions, Empty, Tabs, Tooltip, Row, Badge } from "antd";
import { connect } from "dva";
import { SECURE_PASS_TAG_STATUS } from "@/utils/enum";
import { copyToClipBoard, formatPhoneNumber, getRestrictedDaysOfWeekAsString } from "@/utils/utils";
import HardRouteTag from "@/components/HardRouteTag";
import SecurePassAlert from "@/components/SecurePass/SecurePassAlert/SecurePassAlert";
import SecurePassOutreachAttemptsTable from "@/components/SecurePass/SecurePassOutreachAttemptsTable/SecurePassOutreachAttemptsTable";
import SecurePassSummaryTable from "@/components/SecurePass/SecurePassSummaryTable/SecurePassSummaryTable";
import SecurePassText from "@/components/SecurePass/SecurePassText/SecurePassText";
import TabWithBadge from "@/components/TabWithBadge";
import LocationTag from "@/components/Tags/Location";

const { TabPane } = Tabs;

const TripCard = ({
  tripTime,
  data,
  hardRouteReason,
  handleClickHardRoute,
  adminShipmentView,
  securePassEnabled,
  configPerm,
}) => {
  const adminShipmentData = useMemo(() => adminShipmentView?.data, [adminShipmentView]);

  const tripSpecialConditions = useCallback(val => {
    if (val) {
      return <span style={{ fontWeight: "bold" }}>Yes</span>;
    }
    return <span style={{ fontWeight: "bold" }}>No</span>;
  }, []);

  const countSpecialConditions = useCallback(val => {
    const trueValues = Object.values(val).filter(value => value === true);
    return trueValues.length;
  }, []);

  if (!adminShipmentData) {
    return null;
  }

  return (
    <Card>
      <Tabs defaultActiveKey="1" className="adminShipmentCardTabs">
        <TabPane
          key="1"
          tab={
            <span style={{ padding: 12 }}>
              <span
                style={{
                  padding: 5,
                  marginRight: 12,
                  borderRadius: 5,
                  background: `${tripTime === "Pickup" ? "rgba(0, 0, 0, 0.85)" : "#2d6ac4"}`,
                }}
              >
                {tripTime === "Pickup" ? (
                  <ArrowUpOutlined style={{ margin: 0, color: "white" }} />
                ) : (
                  <ArrowDownOutlined style={{ margin: 0, color: "white" }} />
                )}
              </span>
              {tripTime} Information
            </span>
          }
        >
          <Descriptions
            column={1}
            title={
              <Row justify="space-between" align="middle">
                <div>
                  <Tooltip color="#fff" title="Copy to clipboard">
                    <span
                      style={{ cursor: "pointer" }}
                      onClick={() => {
                        copyToClipBoard(data.name);
                      }}
                    >
                      {data.name}
                    </span>
                  </Tooltip>

                  <Tooltip color="#fff" title="Hard route">
                    <img
                      src="/icons/colorful-speedometer.png"
                      alt="colorful-speedometer"
                      onClick={handleClickHardRoute}
                      style={{
                        width: "24px",
                        height: "24px",
                        marginLeft: "8px",
                        cursor: "pointer",
                      }}
                    />
                  </Tooltip>
                </div>
                {hardRouteReason !== null && hardRouteReason !== undefined && (
                  <HardRouteTag hardRouteLocation={hardRouteReason} />
                )}
                <LocationTag latitude={data?.latitude} longitude={data?.longitude} />
              </Row>
            }
          >
            <Descriptions.Item style={{ paddingBottom: 0 }}>
              <div>{data.completedAddress}</div>
            </Descriptions.Item>
            <Descriptions.Item label="Contact" style={{ paddingBottom: 0 }}>
              {data.contactName ?? ""}
            </Descriptions.Item>
            <Descriptions.Item label="Phone">
              {formatPhoneNumber(data.contactPhone, data.contactPhoneExtension)}
            </Descriptions.Item>
            <Descriptions.Item label="Day(s) of Week Unavailable" style={{ paddingBottom: 0 }}>
              <div
                style={{
                  minWidth: 125,
                  overflowX: "auto",
                }}
              >
                {getRestrictedDaysOfWeekAsString(data?.dayOfWeekRestricted?.split(","))}
              </div>
            </Descriptions.Item>
          </Descriptions>
        </TabPane>
        <TabPane key="2" tab={<TabWithBadge title={`${tripTime} Notes`} count={data.notes ? 1 : 0} />}>
          {data.notes ? (
            <Descriptions bordered column={1}>
              <Descriptions.Item label="Instructions">{data.notes}</Descriptions.Item>
            </Descriptions>
          ) : (
            <Empty style={{ paddingTop: 6 }} image={Empty.PRESENTED_IMAGE_SIMPLE} />
          )}
        </TabPane>
        {tripTime === "Pickup" && (
          <TabPane
            key="3"
            tab={
              <TabWithBadge
                title="Special Conditions"
                color="#FAAD14"
                count={countSpecialConditions(data.specialConditions) || 0}
              />
            }
          >
            <Descriptions column={1}>
              <Descriptions.Item style={{ paddingBottom: 0, paddingTop: 5 }} label="Requires TWIC">
                {tripSpecialConditions(data.specialConditions.requiresTWIC)}
              </Descriptions.Item>
              <Descriptions.Item style={{ paddingBottom: 0, paddingTop: 5 }} label="Requires EModal">
                {tripSpecialConditions(data.specialConditions.requiresEModal)}
              </Descriptions.Item>
              <Descriptions.Item style={{ paddingBottom: 0, paddingTop: 5 }} label="Requires Lot Exit">
                {tripSpecialConditions(data.specialConditions.requiresLotExit)}
              </Descriptions.Item>
            </Descriptions>
          </TabPane>
        )}
        {securePassEnabled && tripTime === "Pickup" && (
          <TabPane
            tab={
              <span>
                <SecurePassText />
                <Badge
                  count={adminShipmentData?.securePass === SECURE_PASS_TAG_STATUS.Enabled ? 1 : 0}
                  showZero
                  style={{
                    marginLeft: 5,
                    backgroundColor:
                      adminShipmentData?.securePass === SECURE_PASS_TAG_STATUS.Enabled ? "red" : "#d9d9d9",
                  }}
                />
              </span>
            }
            key="4"
          >
            <SecurePassAlert
              securePassValue={adminShipmentData?.securePass}
              contactEmail={data.contactEmail}
              shipmentStatus={adminShipmentData?.status}
              loadStatus={adminShipmentData?.load?.status}
            />
            {adminShipmentData?.securePass === SECURE_PASS_TAG_STATUS.Enabled && (
              <SecurePassSummaryTable shipmentId={adminShipmentData?.id} />
            )}
            {!!configPerm["MediaService.SecurePassLogs"] &&
              adminShipmentData?.securePass === SECURE_PASS_TAG_STATUS.MissingContact && (
                <SecurePassOutreachAttemptsTable shipmentId={adminShipmentData?.id} />
              )}
          </TabPane>
        )}
      </Tabs>
    </Card>
  );
};

export default connect(({ adminShipmentView, configs }) => ({
  adminShipmentView,
  securePassEnabled: configs.securePass.enabled,
  configPerm: configs.configPerm,
}))(TripCard);
